import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getData } from "../../../FetchApiServices";

// ─── Contexts ─────────────────────────────────────────────────────────────────
const ToastContext      = createContext(null);
const ConfirmContext    = createContext(null);
const PermissionContext = createContext({ hasPermission: () => true, permissions: {}, loading: true });

let toastId = 0;

export const T2TUIProvider = ({ children }) => {
  const [toasts,  setToasts]  = useState([]);
  const [confirm, setConfirm] = useState(null); // { message, title, resolve }
  const resolveRef = useRef(null);

  // ── Permissions ──
  const user = useSelector((state) => state?.user?.users[0]);
  const [perms, setPerms] = useState({});
  const [permsLoading, setPermsLoading] = useState(true);

  useEffect(() => {
    // 1. Try from Redux user data (role_permissions or permissions field)
    const fromUser = user?.permissions || user?.role_permissions || null;
    if (fromUser && typeof fromUser === "object") {
      setPerms(fromUser);
      setPermsLoading(false);
      return;
    }
    // 2. Fallback: fetch from API
    const fetchPerms = async () => {
      const resp = await getData("users/me/permissions");
      if (!resp.error) {
        const d = resp.data?.data || resp.data || {};
        setPerms(d);
      }
      setPermsLoading(false);
    };
    fetchPerms();
  }, [user]);

  // Admin role bypasses admin-scope permission checks, but NOT user-scope
  // actions (operations.*) — those are personal workflow-participation
  // features an admin shouldn't automatically get unless explicitly granted.
  const isAdmin = (user?.t2t_role_name || "").toLowerCase().includes("admin");
  const isUserScopeFlag = (flag) => typeof flag === "string" && flag.startsWith("operations.");

  const hasPermission = useCallback(
    (flag) => {
      // Admin bypass — admin roles always see admin-scope features
      if (isAdmin && !isUserScopeFlag(flag)) return true;
      // While loading, allow everything so UI doesn't flash empty
      if (permsLoading) return true;
      // If no permissions set at all (empty {}), allow everything — bootstrap mode
      if (Object.keys(perms).length === 0) return true;
      // Once permissions exist: flag must be explicitly true
      return perms[flag] === true;
    },
    [perms, permsLoading, isAdmin]
  );

  // ── Toast ──
  const toast = useCallback((message, type = "info") => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Confirm ──
  const showConfirm = useCallback((message, title = "Are you sure?") => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirm({ message, title });
    });
  }, []);

  const handleConfirmChoice = (result) => {
    setConfirm(null);
    if (resolveRef.current) resolveRef.current(result);
    resolveRef.current = null;
  };

  const ICONS = { success: "✓", error: "✕", warning: "⚠", info: "ℹ" };

  return (
    <PermissionContext.Provider value={{ hasPermission, permissions: perms, loading: permsLoading }}>
    <ToastContext.Provider value={toast}>
      <ConfirmContext.Provider value={showConfirm}>
        {children}

        {/* ── Toast Stack ── */}
        <div className="t2t-toast-stack">
          {toasts.map((t) => (
            <div key={t.id} className={`t2t-toast t2t-toast-${t.type}`}>
              <span className="t2t-toast-icon">{ICONS[t.type]}</span>
              <span className="t2t-toast-msg">{t.message}</span>
              <button className="t2t-toast-close" onClick={() => dismissToast(t.id)}>✕</button>
            </div>
          ))}
        </div>

        {/* ── Confirm Modal ── */}
        {confirm && (
          <div className="t2t-overlay" style={{ zIndex: 9999 }} onClick={() => handleConfirmChoice(false)}>
            <div className="t2t-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="t2t-confirm-title">{confirm.title}</div>
              <div className="t2t-confirm-msg">{confirm.message}</div>
              <div className="t2t-confirm-foot">
                <button className="t2t-btn t2t-btn-ghost" onClick={() => handleConfirmChoice(false)}>Cancel</button>
                <button className="t2t-btn t2t-btn-danger" onClick={() => handleConfirmChoice(true)}>Confirm</button>
              </div>
            </div>
          </div>
        )}
      </ConfirmContext.Provider>
    </ToastContext.Provider>
    </PermissionContext.Provider>
  );
};

T2TUIProvider.propTypes = { children: PropTypes.node.isRequired };

// ─── Hooks ────────────────────────────────────────────────────────────────────
export const useToast        = () => useContext(ToastContext);
export const useConfirm      = () => useContext(ConfirmContext);
export const usePermissions  = () => useContext(PermissionContext);
